import { PRECISION } from './constants';
import { Hole, HoleLocation } from './hole';
import { Part, SVG_NS, ViewContext } from './part';

export interface RimProps {
  erd: number;
  thickness: number;
  numHoles: number;
  holeDiameter: number;
  drillingOffsetDs: number;
  drillingOffsetNds: number;
  flipOffsetOrder: boolean;
}

export class Rim extends Part {
  private nipple: Hole;
  private holes: Hole[];
  constructor(
    public id: string,
    public props: RimProps
  ) {
    super();
    this.nipple = this.generateNipple();
    this.holes = this.generateHoles();
  }

  get children() {
    return [this.nipple, ...this.holes];
  }

  update(props: Partial<RimProps>): void {
    this.props = {
      ...this.props,
      ...props,
    };
    this.nipple = this.generateNipple(); // can be safely regenerated
    const newHoles = this.generateHoles();
    const n = Math.max(newHoles.length, this.holes.length);
    for (let i = 0; i < n; i++) {
      const oldHole = this.holes[i];
      const newHole = newHoles[i];
      if (oldHole && newHole) {
        oldHole.update(newHole.props);
      } else if (!oldHole && newHole) {
        this.holes[i] = newHole;
      } else if (oldHole && !newHole) {
        this.holes = this.holes.slice(0, i);
        break;
      }
    }
  }

  private generateNipple(): Hole {
    return new Hole('NIPPLE', {
      angle: 0,
      distance: this.props.erd / 2,
      diameter: this.props.holeDiameter * 1.5,
      location: 'nipple',
      selectable: false,
    });
  }

  private generateHoles(): Hole[] {
    const holes: Hole[] = [];
    const angleStep = 360 / this.props.numHoles;
    const initAngle = angleStep / 2;
    for (let i = 0; i < this.props.numHoles; i++) {
      const angle = initAngle + angleStep * i;
      let location: HoleLocation = 'rim';
      let centerOffset = 0;
      if (i % 2 < 1 && this.props.drillingOffsetDs > 0) {
        location = this.props.flipOffsetOrder ? 'rim-nds' : 'rim-ds';
        centerOffset = this.props.drillingOffsetDs;
      } else if (i % 2 > 0 && this.props.drillingOffsetNds > 0) {
        location = this.props.flipOffsetOrder ? 'rim-ds' : 'rim-nds';
        centerOffset = this.props.drillingOffsetNds;
      }
      holes.push(
        new Hole(`R${i + 1}`, {
          angle,
          distance: this.props.erd / 2,
          diameter: this.props.holeDiameter,
          location,
          centerOffset,
        })
      );
    }
    return holes;
  }

  render(viewCtx: ViewContext): SVGElement {
    const s = viewCtx.scaler;
    const group = document.createElementNS(SVG_NS, 'g');
    group.classList.add('rim');
    group.setAttribute('id', this.id);
    const outerRimDiameter = viewCtx.schematicView
      ? s.schematicRimOuterDiameter
      : s.pxPerMm * (this.props.erd + this.props.thickness / 2);
    const innerRimDiameter = viewCtx.schematicView
      ? s.schematicRimInnerDiameter
      : s.pxPerMm * (this.props.erd - this.props.thickness / 2);
    const outerCircle = document.createElementNS(SVG_NS, 'circle');
    outerCircle.classList.add('select-resetting', 'rim-outer');
    outerCircle.setAttribute('cx', '0');
    outerCircle.setAttribute('cy', '0');
    outerCircle.setAttribute('r', (outerRimDiameter / 2).toFixed(PRECISION));
    const innerCircle = document.createElementNS(SVG_NS, 'circle');
    innerCircle.classList.add('select-resetting', 'rim-inner');
    innerCircle.setAttribute('cx', '0');
    innerCircle.setAttribute('cy', '0');
    innerCircle.setAttribute('r', (innerRimDiameter / 2).toFixed(PRECISION));
    group.appendChild(outerCircle);
    group.appendChild(innerCircle);
    this.children.forEach((child) => {
      group.appendChild(child.render(viewCtx));
    });
    return group;
  }
}
